import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from "react";
import axios from "axios";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { RotatingLines } from "react-loader-spinner";
import Select from "react-select";
import { debounce } from "lodash";
import "./CourseMaster.css";
import ImageResize from 'quill-image-resize';
// Custom image resize module
//const ImageResize = require('quill-image-resize-module').default;
Quill.register('modules/imageResize', ImageResize);

export default function CourseDetailsMaster() {
    // State management
    const [courseDetails, setCourseDetails] = useState([]);
    const [dropdownOptions, setDropdownOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterCourse, setFilterCourse] = useState("all");
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage] = useState(10);
    const [editorReady, setEditorReady] = useState(false);
    const [files, setFiles] = useState({
        lessonFile: null,
        transcriptFile: null
    });

    const [formData, setFormData] = useState({
        courseDetailId: 0,
        courseSubdetailsId: 0,
        courseId: 0,
        courseHeading: "",
        courseContent: "",
        courseDetailStatus: true,
        courseContentPriority: 0,
        courseAllotedTime: "",
        courseLessonContent: "",
        courseTrascriptContent: "",
        lessonUrl: "",
        transcriptUrl: ""
    });

    // Refs
    const quillRef = useRef(null);
    const quillLessonRef = useRef(null);
    const quillTranscriptRef = useRef(null);
    const quillInstanceRef = useRef(null);
    const quillLessonInstanceRef = useRef(null);
    const quillTranscriptInstanceRef = useRef(null);
    const modalRef = useRef(null);

    // Helper function to get headers
    const getHeaders = useCallback(
        () => ({
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("token"),
        }),
        []
    );

    // API calls
    const fetchDropdownOptions = useCallback(async () => {
        try {
            const response = await axios.get(
                `${API_LoginURL}Course/CourseSubDetailsList`,
                { headers: getHeaders() }
            );
            setDropdownOptions(
                Array.isArray(response.data) ? response.data : []
            );
        } catch (error) {
            console.error("Error fetching dropdown options:", error);
            Swal.fire("Error!", "Failed to load course topics.", "error");
            throw error;
        }
    }, [getHeaders]);

    const fetchCourseDetails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${API_LoginURL}Course/CourseDetailsList`,
                { headers: getHeaders() }
            );
            setCourseDetails(Array.isArray(response.data) ? response.data : []);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching course details:", error);
            Swal.fire("Error!", "Failed to load course details.", "error");
            setLoading(false);
            throw error;
        }
    }, [getHeaders]);

    // Initialize Quill editors
    useEffect(() => {
        const initializeEditors = () => {
            const toolbarOptions = [
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote', 'code-block'],
                [{ 'header': 1 }, { 'header': 2 }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'script': 'sub' }, { 'script': 'super' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                [{ 'direction': 'ltr' }],
                [{ 'size': ['small', false, 'large', 'huge'] }],
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'font': [] }],
                [{ 'align': [] }],
                ['clean'],
                ['link', 'image', 'video']
            ];

            // Main content editor
            if (quillRef.current && !quillInstanceRef.current) {
                quillInstanceRef.current = new Quill(quillRef.current, {
                    theme: "snow",
                    placeholder: "Enter course content...",
                    modules: {
                        toolbar: toolbarOptions,
                        imageResize: {
                            displaySize: true
                        }
                    }
                });

                quillInstanceRef.current.on('text-change', () => {
                    setFormData(prev => ({
                        ...prev,
                        courseContent: quillInstanceRef.current.root.innerHTML
                    }));
                });
            }

            // Lesson content editor
            if (quillLessonRef.current && !quillLessonInstanceRef.current) {
                quillLessonInstanceRef.current = new Quill(quillLessonRef.current, {
                    theme: "snow",
                    placeholder: "Enter lesson content...",
                    modules: {
                        toolbar: toolbarOptions,
                        imageResize: {
                            displaySize: true
                        }
                    }
                });

                quillLessonInstanceRef.current.on('text-change', () => {
                    setFormData(prev => ({
                        ...prev,
                        courseLessonContent: quillLessonInstanceRef.current.root.innerHTML
                    }));
                });
            }

            // Transcript content editor
            if (quillTranscriptRef.current && !quillTranscriptInstanceRef.current) {
                quillTranscriptInstanceRef.current = new Quill(quillTranscriptRef.current, {
                    theme: "snow",
                    placeholder: "Enter transcript content...",
                    modules: {
                        toolbar: toolbarOptions,
                        imageResize: {
                            displaySize: true
                        }
                    }
                });

                quillTranscriptInstanceRef.current.on('text-change', () => {
                    setFormData(prev => ({
                        ...prev,
                        courseTrascriptContent: quillTranscriptInstanceRef.current.root.innerHTML
                    }));
                });
            }

            setEditorReady(true);
        };

        initializeEditors();

        return () => {
            if (quillInstanceRef.current) {
                quillInstanceRef.current.off('text-change');
            }
            if (quillLessonInstanceRef.current) {
                quillLessonInstanceRef.current.off('text-change');
            }
            if (quillTranscriptInstanceRef.current) {
                quillTranscriptInstanceRef.current.off('text-change');
            }
        };
    }, []);

    // Initialize data
    useEffect(() => {
        const initializeAll = async () => {
            try {
                await fetchDropdownOptions();
                await fetchCourseDetails();
            } catch (error) {
                console.error("Initialization error:", error);
            }
        };

        initializeAll();
    }, [fetchCourseDetails, fetchDropdownOptions]);

    // Handle modal show events
    useEffect(() => {
        const modal = document.getElementById('courseDetailsModal');
        if (!modal) return;

        const handleShow = () => {
            if (quillInstanceRef.current && formData.courseContent) {
                quillInstanceRef.current.root.innerHTML = formData.courseContent;
            }
            if (quillLessonInstanceRef.current && formData.courseLessonContent) {
                quillLessonInstanceRef.current.root.innerHTML = formData.courseLessonContent;
            }
            if (quillTranscriptInstanceRef.current && formData.courseTrascriptContent) {
                quillTranscriptInstanceRef.current.root.innerHTML = formData.courseTrascriptContent;
            }
        };

        modal.addEventListener('shown.bs.modal', handleShow);

        return () => {
            modal.removeEventListener('shown.bs.modal', handleShow);
        };
    }, [formData.courseContent, formData.courseLessonContent, formData.courseTrascriptContent]);

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleFileChange = (e) => {
        const { name, files: fileList } = e.target;
        setFiles(prev => ({
            ...prev,
            [name]: fileList[0]
        }));
    };

    const handleSelectChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            courseSubdetailsId: selectedOption ? selectedOption.value : 0,
            courseId: selectedOption ? selectedOption.courseId : 0
        }));
    };

    // Debounced search
    const debouncedSearch = useMemo(
        () =>
            debounce((query) => {
                setSearchQuery(query);
                setCurrentPage(0);
            }, 300),
        []
    );

    const handleSearchChange = (e) => {
        debouncedSearch(e.target.value);
    };

    const handleFilterChange = (e) => {
        setFilterStatus(e.target.value);
        setCurrentPage(0);
    };

    const handleCourseFilterChange = (e) => {
        setFilterCourse(e.target.value);
        setCurrentPage(0);
    };

    const resetForm = useCallback(() => {
        setFormData({
            courseDetailId: 0,
            courseSubdetailsId: 0,
            courseId: 0,
            courseHeading: "",
            courseContent: "",
            courseDetailStatus: true,
            courseContentPriority: 0,
            courseAllotedTime: "",
            courseLessonContent: "",
            courseTrascriptContent: "",
            lessonUrl: "",
            transcriptUrl: ""
        });
        
        setFiles({
            lessonFile: null,
            transcriptFile: null
        });

        if (quillInstanceRef.current) {
            quillInstanceRef.current.root.innerHTML = "";
        }
        if (quillLessonInstanceRef.current) {
            quillLessonInstanceRef.current.root.innerHTML = "";
        }
        if (quillTranscriptInstanceRef.current) {
            quillTranscriptInstanceRef.current.root.innerHTML = "";
        }
    }, []);

    const handleEdit = useCallback((detail) => {
        setFormData({
            courseDetailId: detail.courseDetailId,
            courseSubdetailsId: detail.courseSubdetailsId,
            courseId: detail.courseId,
            courseHeading: detail.courseHeading,
            courseContent: detail.courseContent || "",
            courseDetailStatus: detail.courseDetailStatus !== false,
            courseContentPriority: detail.courseContentPriority || 0,
            courseAllotedTime: detail.courseAllotedTime || "",
            courseLessonContent: detail.courseLessonContent || "",
            courseTrascriptContent: detail.courseTrascriptContent || "",
            lessonUrl: detail.lessonUrl || "",
            transcriptUrl: detail.transcriptUrl || ""
        });

        setTimeout(() => {
            if (quillInstanceRef.current) {
                quillInstanceRef.current.root.innerHTML = detail.courseContent || "";
            }
            if (quillLessonInstanceRef.current) {
                quillLessonInstanceRef.current.root.innerHTML = detail.courseLessonContent || "";
            }
            if (quillTranscriptInstanceRef.current) {
                quillTranscriptInstanceRef.current.root.innerHTML = detail.courseTrascriptContent || "";
            }
        }, 300);
    }, []);

    const handleDelete = async (courseDetailId) => {
        const confirmation = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (!confirmation.isConfirmed) return;

        try {
            await axios.post(
                `${API_LoginURL}Course/DeleteCourseDetails`,
                { courseDetailsId: courseDetailId },
                { headers: getHeaders() }
            );
            setCourseDetails((prev) =>
                prev.filter(
                    (detail) => detail.courseDetailId !== courseDetailId
                )
            );
            Swal.fire("Deleted!", "Course detail has been deleted.", "success");
        } catch (error) {
            console.error("Error deleting course detail:", error);
            Swal.fire("Error", "Failed to delete course detail", "error");
        }
    };

    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.courseSubdetailsId || !formData.courseHeading) {
            Swal.fire("Error", "Please fill all required fields", "error");
            return;
        }
    
        setFormSubmitting(true);
        try {
            const formDataToSend = new FormData();
            
            // Append all form data including transcriptUrl
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    // Ensure boolean values are converted to strings
                    const value = typeof formData[key] === 'boolean' 
                        ? formData[key].toString() 
                        : formData[key];
                    formDataToSend.append(key, value);
                }
            });
            
            // Append files if they exist
            if (files.lessonFile) {
                formDataToSend.append('CouseLessonFile', files.lessonFile);
            }
            if (files.transcriptFile) {
                formDataToSend.append('CouseTranscriptFile', files.transcriptFile);
            }
            
            // Get HTML content from editors
            formDataToSend.set('courseContent', quillInstanceRef.current?.root.innerHTML || formData.courseContent);
            formDataToSend.set('courseLessonContent', quillLessonInstanceRef.current?.root.innerHTML || formData.courseLessonContent);
            formDataToSend.set('courseTrascriptContent', quillTranscriptInstanceRef.current?.root.innerHTML || formData.courseTrascriptContent);
    
            // Debug: Log the FormData before sending
            for (let [key, value] of formDataToSend.entries()) {
                console.log(key, value);
            }
    
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: localStorage.getItem("token")
                }
            };
    
            const response = await axios.post(
                `${API_LoginURL}Course/CourseDetailsAddEdit`,
                formDataToSend,
                config
            );
    
            if (response.data.response === "SUCCESS") {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Course detail saved successfully",
                });
                await fetchCourseDetails();
                resetForm();
                document.getElementById("closeCourseDetailsModal").click();
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response.data.response || "Failed to save course detail",
                });
            }
        } catch (error) {
            console.error("Error saving course detail:", error);
            Swal.fire("Error", "Failed to save course detail", "error");
        } finally {
            setFormSubmitting(false);
        }
    };

    // Get unique course names for filter
    const uniqueCourseNames = useMemo(() => {
        const courses = dropdownOptions.map(option => option.courseName);
        return [...new Set(courses)];
    }, [dropdownOptions]);

    // Filter and pagination
    const filteredDetails = useMemo(() => {
        return courseDetails.filter((detail) => {
            const topicOption = dropdownOptions.find(
                option => option.courseSubdetailsId === detail.courseSubdetailsId
            );
            
            const topic = topicOption?.courseTopicName || "";
            const courseName = topicOption?.courseName || "";

            const matchesSearch =
                detail.courseHeading
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
                courseName.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesStatus =
                filterStatus === "all" ||
                (filterStatus === "active" && detail.courseDetailStatus === true) ||
                (filterStatus === "inactive" && detail.courseDetailStatus === false);

            const matchesCourse =
                filterCourse === "all" ||
                courseName === filterCourse;

            return matchesSearch && matchesStatus && matchesCourse;
        });
    }, [courseDetails, dropdownOptions, searchQuery, filterStatus, filterCourse]);

    const pageCount = Math.ceil(filteredDetails.length / itemsPerPage);
    const displayedDetails = useMemo(() => {
        return filteredDetails.slice(
            currentPage * itemsPerPage,
            (currentPage + 1) * itemsPerPage
        );
    }, [filteredDetails, currentPage, itemsPerPage]);

    // Get topic name for display
    const getTopicName = (courseSubdetailsId) => {
        const topic = dropdownOptions.find(
            (option) => option.courseSubdetailsId === courseSubdetailsId
        );
        return topic ? `${topic.courseTopicName} (${topic.courseName})` : "N/A";
    };

    return (
        <div className="container-scroller">
            <Navbar />
            <div className="container-fluid page-body-wrapper">
                <SASidebar />
                <div className="main-panel">
                    <div className="content-wrapper">
                        <div className="col-lg-12 grid-margin stretch-card">
                            <div className="card">
                                <div className="card-body">
                                    {/* Header Section */}
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                                        <h4 className="card-title mb-3 mb-md-0">
                                            Course Details Management
                                        </h4>

                                        {/* Search and Filter */}
                                        <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                                            {/* Search */}
                                            <div
                                                className="flex-grow-1 me-md-2 position-relative"
                                                style={{ maxWidth: "500px" }}
                                            >
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm ps-5 rounded-pill border-0"
                                                    placeholder="Search by Heading, Topic or Course"
                                                    onChange={
                                                        handleSearchChange
                                                    }
                                                    style={{
                                                        height: "38px",
                                                        boxShadow:
                                                            "0 2px 8px rgba(0,0,0,0.08)",
                                                        transition:
                                                            "all 0.3s ease",
                                                        backgroundColor:
                                                            "#f8f9fa",
                                                    }}
                                                    onFocus={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 12px rgba(0,0,0,0.15)";
                                                        e.target.style.backgroundColor =
                                                            "#fff";
                                                    }}
                                                    onBlur={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 8px rgba(0,0,0,0.08)";
                                                        e.target.style.backgroundColor =
                                                            "#f8f9fa";
                                                    }}
                                                />
                                                <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                            </div>

                                            {/* Filters */}
                                            <div className="d-flex align-items-center gap-2">
                                                {/* Course Filter */}
                                                <div className="d-flex align-items-center">
                                                    <i className="fas fa-book me-2 text-muted"></i>
                                                    <select
                                                        className="form-select"
                                                        value={filterCourse}
                                                        onChange={handleCourseFilterChange}
                                                        style={{
                                                            minWidth: "120px",
                                                        }}
                                                    >
                                                        <option value="all">All Courses</option>
                                                        {uniqueCourseNames.map((course, index) => (
                                                            <option key={index} value={course}>
                                                                {course}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Status Filter */}
                                                {/* <div className="d-flex align-items-center">
                                                    <i className="fas fa-filter me-2 text-muted"></i>
                                                    <select
                                                        className="form-select"
                                                        value={filterStatus}
                                                        onChange={
                                                            handleFilterChange
                                                        }
                                                        style={{
                                                            minWidth: "120px",
                                                        }}
                                                    >
                                                        <option value="all">
                                                            All Status
                                                        </option>
                                                        <option value="active">
                                                            Active
                                                        </option>
                                                        <option value="inactive">
                                                            Inactive
                                                        </option>
                                                    </select>
                                                </div> */}
                                            </div>

                                            {/* Add Detail Button */}
                                            <button
                                                className="btn btn-primary d-flex align-items-center"
                                                data-bs-toggle="modal"
                                                data-bs-target="#courseDetailsModal"
                                                onClick={resetForm}
                                            >
                                                <i className="fas fa-plus me-2"></i>
                                                <span className="d-none d-md-inline">
                                                    Add Detail
                                                </span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Course Details Table */}
                                    <div className="table-responsive">
                                        {loading ? (
                                            <div
                                                className="d-flex justify-content-center align-items-center"
                                                style={{ height: "200px" }}
                                            >
                                                <RotatingLines
                                                    strokeColor="#4e73df"
                                                    strokeWidth="5"
                                                    animationDuration="0.75"
                                                    width="50"
                                                />
                                                <p className="mt-3 ms-3">
                                                    Loading course details...
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <table className="table table-hover table-striped">
                                                    <thead className="table-primary">
                                                        <tr>
                                                            <th width="10%">
                                                                ID
                                                            </th>
                                                            <th width="10%">
                                                                Course Name
                                                            </th>
                                                            <th width="25%">
                                                                Topic
                                                            </th>
                                                            <th width="35%">
                                                                Heading
                                                            </th>
                                                            <th width="10%">
                                                                Priority
                                                            </th>
                                                            <th
                                                                width="10%"
                                                                className="text-end"
                                                            >
                                                                Actions
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displayedDetails.length > 0 ? (
                                                            displayedDetails.map(
                                                                (detail) => (
                                                                    <tr
                                                                        key={
                                                                            detail.courseDetailId
                                                                        }
                                                                    >
                                                                        <td>
                                                                            {
                                                                                detail.courseDetailId
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                dropdownOptions.filter(
                                                                                    (
                                                                                        course
                                                                                    ) =>
                                                                                        course.courseSubdetailsId ===
                                                                                        detail.courseSubdetailsId
                                                                                )[0]
                                                                                    .courseName
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {getTopicName(
                                                                                detail.courseSubdetailsId
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                detail.courseHeading
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                detail.courseContentPriority
                                                                            }
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <div className="d-flex justify-content-end gap-2">
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    data-bs-toggle="modal"
                                                                                    data-bs-target="#courseDetailsModal"
                                                                                    onClick={() =>
                                                                                        handleEdit(
                                                                                            detail
                                                                                        )
                                                                                    }
                                                                                    title="Edit"
                                                                                >
                                                                                    <i className="fas fa-edit"></i>
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                    onClick={() =>
                                                                                        handleDelete(
                                                                                            detail.courseDetailId
                                                                                        )
                                                                                    }
                                                                                    title="Delete"
                                                                                >
                                                                                    <i className="fas fa-trash"></i>
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            )
                                                        ) : (
                                                            <tr>
                                                                <td
                                                                    colSpan="6"
                                                                    className="text-center py-4"
                                                                >
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <img
                                                                            src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                                                            alt="No details"
                                                                            style={{
                                                                                opacity: 0.5,
                                                                                width: "60px",
                                                                            }}
                                                                        />
                                                                        <p className="mt-3 text-muted">
                                                                            No course details found
                                                                        </p>
                                                                        <button
                                                                            className="btn btn-sm btn-primary mt-2"
                                                                            data-bs-toggle="modal"
                                                                            data-bs-target="#courseDetailsModal"
                                                                            onClick={
                                                                                resetForm
                                                                            }
                                                                        >
                                                                            Add New Detail
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>

                                                {/* Pagination */}
                                                {displayedDetails.length > 0 && (
                                                    <ReactPaginate
                                                        previousLabel={
                                                            <i className="fas fa-chevron-left"></i>
                                                        }
                                                        nextLabel={
                                                            <i className="fas fa-chevron-right"></i>
                                                        }
                                                        pageCount={pageCount}
                                                        onPageChange={
                                                            handlePageChange
                                                        }
                                                        containerClassName={
                                                            "pagination justify-content-center mt-4"
                                                        }
                                                        pageClassName={
                                                            "page-item"
                                                        }
                                                        pageLinkClassName={
                                                            "page-link"
                                                        }
                                                        previousClassName={
                                                            "page-item"
                                                        }
                                                        previousLinkClassName={
                                                            "page-link"
                                                        }
                                                        nextClassName={
                                                            "page-item"
                                                        }
                                                        nextLinkClassName={
                                                            "page-link"
                                                        }
                                                        activeClassName={
                                                            "active"
                                                        }
                                                        breakLabel={"..."}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={5}
                                                        forcePage={currentPage}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Course Details Modal */}
            <div
                className="modal fade"
                id="courseDetailsModal"
                tabIndex="-1"
                aria-labelledby="courseDetailsModalLabel"
                aria-hidden="true"
                ref={modalRef}
            >
                <div className="modal-dialog modal-xl">
                    <div className="modal-content">
                        <div className="modal-header bg-light">
                            <h5
                                className="modal-title"
                                id="courseDetailsModalLabel"
                            >
                                {formData.courseDetailId > 0
                                    ? "Edit Course Detail"
                                    : "Add Course Detail"}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={resetForm}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleFormSubmit}>
                                <div className="row decrease_gap">
                                    <div className="col-md-6 mb-3">
                                        <label
                                            htmlFor="courseSubdetailsId"
                                            className="form-label required"
                                        >
                                            Topic
                                        </label>
                                        <Select
                                            id="courseSubdetailsId"
                                            options={dropdownOptions.map(
                                                (option) => ({
                                                    value: option.courseSubdetailsId,
                                                    label: `${option.courseTopicName} (${option.courseName})`,
                                                    courseId: option.courseId
                                                })
                                            )}
                                            value={
                                                dropdownOptions.find(
                                                    (option) =>
                                                        option.courseSubdetailsId ===
                                                        formData.courseSubdetailsId
                                                )
                                                    ? {
                                                        value: formData.courseSubdetailsId,
                                                        label: `${dropdownOptions.find(
                                                            o => o.courseSubdetailsId === formData.courseSubdetailsId
                                                        )?.courseTopicName || ''} (${
                                                            dropdownOptions.find(
                                                                o => o.courseSubdetailsId === formData.courseSubdetailsId
                                                            )?.courseName || ''
                                                        })`,
                                                        courseId: formData.courseId
                                                    }
                                                    : null
                                            }
                                            onChange={handleSelectChange}
                                            placeholder="Select Topic"
                                            className="basic-single"
                                            classNamePrefix="select"
                                            isClearable
                                            isSearchable
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label
                                            htmlFor="courseHeading"
                                            className="form-label required"
                                        >
                                            Heading
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="courseHeading"
                                            name="courseHeading"
                                            value={formData.courseHeading}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label
                                            htmlFor="courseContentPriority"
                                            className="form-label"
                                        >
                                            Priority
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control no-spinners"
                                            id="courseContentPriority"
                                            name="courseContentPriority"
                                            value={
                                                formData.courseContentPriority
                                            }
                                            onChange={handleInputChange}
                                            min="0"
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3 d-none">
                                        <label
                                            htmlFor="courseAllotedTime"
                                            className="form-label"
                                        >
                                            Alloted Time
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="courseAllotedTime"
                                            name="courseAllotedTime"
                                            value={formData.courseAllotedTime}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 30 minutes"
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label
                                            htmlFor="lessonUrl"
                                            className="form-label"
                                        >
                                            Lesson URL
                                        </label>
                                        <input
                                            type="url"
                                            className="form-control"
                                            id="lessonUrl"
                                            name="lessonUrl"
                                            value={formData.lessonUrl}
                                            onChange={handleInputChange}
                                            placeholder="https://example.com/lesson"
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label
                                            htmlFor="transcriptUrl"
                                            className="form-label"
                                        >
                                            Transcript URL
                                        </label>
                                        <input
                                            type="url"
                                            className="form-control"
                                            id="transcriptUrl"
                                            name="transcriptUrl"
                                            value={formData.transcriptUrl}
                                            onChange={handleInputChange}
                                            placeholder="https://example.com/transcript"
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label
                                            htmlFor="lessonFile"
                                            className="form-label"
                                        >
                                            Lesson File
                                        </label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            id="lessonFile"
                                            name="lessonFile"
                                            onChange={handleFileChange}
                                            accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label
                                            htmlFor="transcriptFile"
                                            className="form-label"
                                        >
                                            Transcript File
                                        </label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            id="transcriptFile"
                                            name="transcriptFile"
                                            onChange={handleFileChange}
                                            accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
                                        />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label
                                            htmlFor="courseContent"
                                            className="form-label"
                                        >
                                            Content
                                        </label>
                                        <div
                                            ref={quillRef}
                                            style={{ height: "200px", marginBottom: "20px" }}
                                        />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label
                                            htmlFor="courseLessonContent"
                                            className="form-label"
                                        >
                                            Lesson Content
                                        </label>
                                        <div
                                            ref={quillLessonRef}
                                            style={{ height: "200px", marginBottom: "20px" }}
                                        />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label
                                            htmlFor="courseTrascriptContent"
                                            className="form-label"
                                        >
                                            Transcript Content
                                        </label>
                                        <div
                                            ref={quillTranscriptRef}
                                            style={{ height: "200px", marginBottom: "20px" }}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label
                                            htmlFor="courseDetailStatus"
                                            className="form-label"
                                        >
                                            Status
                                        </label>
                                        <select
                                            className="form-select"
                                            id="courseDetailStatus"
                                            name="courseDetailStatus"
                                            value={formData.courseDetailStatus}
                                            onChange={handleInputChange}
                                        >
                                            <option value={true}>Active</option>
                                            <option value={false}>Inactive</option>
                                        </select>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                id="closeCourseDetailsModal"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                                onClick={resetForm}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleFormSubmit}
                                disabled={formSubmitting}
                            >
                                {formSubmitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Submitting...
                                    </>
                                ) : formData.courseDetailId > 0 ? (
                                    "Update"
                                ) : (
                                    "Save"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}